import type { ApiFetchWith } from "./apiFetch.js";
import { apiErrorSlug, isApiErrorLike } from "./error.js";
import type { NewWorkout } from "./schemas/workout.js";
import { deleteOrphanSession, SessionLeftError } from "./orphanSession.js";
import { assertSetsStored, setsNotStored, type SetsAnswer } from "./setsStored.js";
import { uuidv4 } from "./uuid.js";
import { splitWorkout } from "./workoutSplit.js";

// Minimal API surface persistWorkout needs — same shape exposed by both
// apps/web-rnw/src/api.ts and apps/mobile/src/api/index.ts, plus the replay key
// documented on PersistWorkoutStep below.
export type ApiFetch = ApiFetchWith<{
  /** Optional. Stable Idempotency-Key for replays. */
  idempotencyKey?: string;
}>;

/**
 * Identifies which API call a Idempotency-Key applies to inside a
 * single persistWorkout run. The mobile outbox uses this to derive
 * a stable key per step from the outbox item id, so a retry of the
 * same item replays each underlying request idempotently.
 */
export type PersistWorkoutStep =
  | { kind: "session" }
  | { kind: "sets-batch"; chunk: number }
  | { kind: "orphan-cleanup" }
  | { kind: "sets-replace" };

export interface PersistWorkoutArgs {
  /** Already-validated NewWorkout — caller runs Zod safeParse. */
  workout: NewWorkout;
  /** When correcting a saved session, its id: it is rewritten in place
   *  (correctWorkout, #38). Otherwise null. */
  editingId: string | null;
  /** With `editingId`: the session's version when the correction began
   *  (sessionVersion, #314). Absent: it overwrites, as from an older app. */
  baseVersion?: string;
  apiFetch: ApiFetch;
  /**
   * Generates the new session id. Caller-controlled so the same id
   * round-trips through any retry / outbox replay path with a stable
   * Idempotency-Key. Web passes `crypto.randomUUID`; mobile passes a
   * crypto.randomUUID under the `react-native-get-random-values`
   * polyfill. Unused by a correction, which keeps `editingId`.
   */
  generateId: () => string;
  /** Optional logging hook, silent by default. */
  onOrphanCleanupFailed?: (sessionId: string, err: unknown) => void;
  /**
   * Optional. Returns the Idempotency-Key for a given step. The
   * mobile outbox supplies stable keys derived from the outbox item
   * id so each retry replays each underlying call idempotently. Web
   * leaves this undefined and apiFetch generates a fresh key per
   * request — every save is a brand new operation.
   */
  idempotencyKeyFor?: (step: PersistWorkoutStep) => string | undefined;
  /** One client id per set, in the order splitWorkout emits them
   *  (`mintSetIds`). A replay sends the same ids, so a set already stored
   *  answers `duplicate_id` instead of landing twice (#286). */
  setIds?: readonly string[];
  /** The queue's replay, mobile only (#286): the session read by id, posted
   *  only when it is not there (`resumeSession`), every set stored
   *  (`assertSetsStored`), nothing deleted on failure. The session and batch
   *  keys are left to the client's apiFetch default: the session is found by
   *  id (or a 409 `duplicate_id`) and the sets dedup on theirs. A stable
   *  session key would answer a cached 201 for 24h after an older app's
   *  orphan cleanup deleted that session, and every set would then come back
   *  `unknown_session`. */
  resumable?: boolean;
  /** With `resumable`: `setIds` were minted after an older app may already
   *  have stored these sets under ids the server chose (a row queued before
   *  #286). When the session already exists, the sets it holds are not sent
   *  again (`setsNotStored`). */
  setsMayBeStored?: boolean;
  /** The direct save (#289): a failure after the session landed deletes
   *  nothing and throws SessionLeftError. The caller deletes the session
   *  unless its queue took the save, whose replay completes it by id. */
  cleanupByCaller?: boolean;
}

export interface PersistWorkoutResult {
  sessionId: string;
  /** Server-stamped updatedAt from the session POST (or correction PUT). */
  serverUpdatedAt: string | undefined;
  /** A correction's: the session's version it left (sessionVersion, #314). */
  serverVersion?: string;
}

const SETS_CHUNK = 100;

/**
 * Native write path used by both web and mobile loggers. Replaces the
 * previous destructive wipe-and-replay (DELETE THEN POST), which left
 * the user with an empty workout when the network died between the
 * two calls — no recovery.
 *
 * Steps:
 *   1. POST /sessions for the envelope.
 *   2. POST /sets/batch in chunks of 100 for the LogSets.
 *   3. Read every set's answer: one not stored fails the save.
 *   4. On failure of step 2 or 3: best-effort DELETE the freshly
 *      created session so we don't leak an orphan; the user can retry.
 *      Not with `resumable`, whose replay completes it instead, nor with
 *      `cleanupByCaller`, whose caller decides (SessionLeftError).
 * A correction (`editingId`) takes correctWorkout instead.
 */
export async function persistWorkout(
  args: PersistWorkoutArgs,
): Promise<PersistWorkoutResult> {
  const {
    workout,
    editingId,
    apiFetch,
    generateId,
    onOrphanCleanupFailed,
    idempotencyKeyFor,
  } = args;
  if (editingId !== null) {
    const { baseVersion } = args;
    return correctWorkout({
      workout,
      sessionId: editingId,
      apiFetch,
      idempotencyKeyFor,
      baseVersion,
    });
  }

  const sessionId = generateId();
  const split = splitWorkout({ ...workout, id: sessionId });
  const { session } = split;
  const sets = withSetIds(split.sets, args.setIds);
  const resumable = args.resumable === true;
  const keyFor = (step: PersistWorkoutStep): string | undefined =>
    idempotencyKeyFor?.(step);

  let sessionPosted = false;
  let serverUpdatedAt: string | undefined;

  try {
    // ⚠️ A direct save takes postSession: there a 409 means this attempt
    // wrote no sets, so it must surface (useSaveWorkout).
    const posted = resumable
      ? await resumeSession(apiFetch, sessionId, session)
      : await postSession(apiFetch, session, keyFor({ kind: "session" }));
    serverUpdatedAt = posted.updatedAt;
    sessionPosted = true;
    const toSend =
      resumable && args.setsMayBeStored === true && posted.existed
        ? await setsNotStored(apiFetch, sessionId, sets)
        : sets;
    // Every chunk goes before their answers are read: a refused set in one
    // never keeps the next from going (#286).
    const answers: SetsAnswer[] = [];
    let chunk = 0;
    for (let i = 0; i < toSend.length; i += SETS_CHUNK) {
      const slice = toSend.slice(i, i + SETS_CHUNK);
      const resp = await apiFetch<unknown>("/api/sets/batch", {
        method: "POST",
        body: slice,
        idempotencyKey: resumable
          ? undefined
          : keyFor({ kind: "sets-batch", chunk }),
      });
      answers.push({ resp, count: slice.length });
      chunk++;
    }
    assertSetsStored(answers);
  } catch (writeErr) {
    // A replay leaves the session for the next try (#286, D4).
    if (!sessionPosted || resumable) throw writeErr;
    if (args.cleanupByCaller === true) throw new SessionLeftError(writeErr, sessionId);
    await deleteOrphanSession(
      apiFetch,
      sessionId,
      keyFor({ kind: "orphan-cleanup" }),
      onOrphanCleanupFailed,
    );
    throw writeErr;
  }

  return { sessionId, serverUpdatedAt };
}

/** One fresh id per set of `workout`, in splitWorkout's order (#286). */
export const mintSetIds = (workout: NewWorkout): string[] =>
  workout.entries.flatMap((entry) => entry.sets.map(() => uuidv4()));

function withSetIds<S>(sets: S[], ids: readonly string[] | undefined): S[] {
  if (ids === undefined) return sets;
  if (ids.length !== sets.length) throw new Error("set_ids_mismatch");
  return sets.map((set, i) => ({ ...set, id: ids[i] }));
}

/**
 * #38, model A: a correction rewrites the saved session under its own id, in
 * ONE request: the envelope and the sets go in the same server transaction
 * (PUT /sessions/:id/sets), so a refused envelope never leaves new sets under
 * an old one. The server replaces the fields the logger owns (a field left
 * out is cleared) and never touches measured calories, heart rate, `source`,
 * `externalId` or `externalOrigin`. With `baseVersion`, a session the athlete
 * wrote since then is not overwritten: the server answers 412
 * (isStaleEtagError).
 */
export async function correctWorkout(args: {
  workout: NewWorkout;
  /** The saved session being corrected; it keeps this id. */
  sessionId: string;
  apiFetch: ApiFetch;
  idempotencyKeyFor?: (step: PersistWorkoutStep) => string | undefined;
  /** The session's version when the correction began (#314). */
  baseVersion?: string;
}): Promise<PersistWorkoutResult> {
  const { workout, sessionId, apiFetch, idempotencyKeyFor, baseVersion } = args;
  const { session, sets } = splitWorkout({ ...workout, id: sessionId });
  const { id: _id, createdAt: _createdAt, ...envelope } = session;
  try {
    const res = await apiFetch<unknown>(
      `/api/sessions/${encodeURIComponent(sessionId)}/sets`,
      {
        method: "PUT",
        body: {
          session: envelope,
          sets,
          ...(baseVersion !== undefined ? { baseVersion } : {}),
        },
        idempotencyKey: idempotencyKeyFor?.({ kind: "sets-replace" }),
      },
    );
    const r = (res && typeof res === "object" ? res : {}) as {
      updatedAt?: unknown;
      loggerUpdatedAt?: unknown;
    };
    const str = (v: unknown) => (typeof v === "string" ? v : undefined);
    const serverVersion = str(r.loggerUpdatedAt);
    return {
      sessionId,
      serverUpdatedAt: str(r.updatedAt),
      ...(serverVersion !== undefined ? { serverVersion } : {}),
    };
  } catch (err) {
    // A 409 here is a refusal (endedAt before startedAt, a saved session
    // reopened), never the "already there" 409 `duplicate_id` means to the
    // outbox.
    // A write that kept colliding is a 503: it stays retryable.
    if (isApiErrorLike(err) && err.status === 409) {
      throw new SessionConflictError(apiErrorSlug(err));
    }
    throw err;
  }
}

/** What a session POST gives back that a caller uses. */
export interface PostedSession {
  /** Server-stamped updatedAt; absent when the session already existed. */
  updatedAt?: string;
  /** True when the server answered 409 `duplicate_id`. */
  existed: boolean;
}

// The one hand-written POST /api/sessions, for persistWorkout and ensureSession.
async function postSession(
  apiFetch: ApiFetch,
  session: unknown,
  idempotencyKey?: string,
): Promise<PostedSession> {
  const resp = await apiFetch<unknown>("/api/sessions", {
    method: "POST",
    body: session,
    idempotencyKey,
  });
  const ru =
    resp && typeof resp === "object"
      ? (resp as { updatedAt?: unknown }).updatedAt
      : undefined;
  return { updatedAt: typeof ru === "string" ? ru : undefined, existed: false };
}

/**
 * A 409 on a session POST that is NOT `duplicate_id`: the server refused, the
 * session is not known to exist. Its own type so the outbox reads it as a
 * refusal (`reject`), never as the "already there" of 409 `duplicate_id`.
 * Duck-typed on `name`, like TagCapError.
 */
export class SessionConflictError extends Error {
  readonly slug: string | undefined;
  constructor(slug: string | undefined) {
    super(`session_conflict_${slug ?? "unknown"}`);
    this.name = "SessionConflictError";
    this.slug = slug;
  }
}

export const isSessionConflictError = (
  err: unknown,
): err is SessionConflictError =>
  err instanceof Error && err.name === "SessionConflictError";

/** Any error shape whose HTTP status is `code`. */
export const hasHttpStatus = (err: unknown, code: number): boolean =>
  typeof err === "object" && err !== null && (err as { status?: unknown }).status === code;

/** A 412: the document was written since the version this write was built
 *  on (`stale_etag`). Reload it, never replay. */
export const isStaleEtagError = (err: unknown): boolean => hasHttpStatus(err, 412);

/** A 426 (`client_outdated`, #336): this save would drop fields the program
 *  uses, and the server refused it. Update the app, never replay. */
export const isClientOutdatedError = (err: unknown): boolean => hasHttpStatus(err, 426);

/**
 * POST a session that may already be on the server, for a replay that must
 * converge (#42, #286): 201 creates it, 409 `duplicate_id` says it is there.
 * Either way it exists afterwards and the caller goes on to its sets. Any
 * other 409 throws SessionConflictError; any other error throws as it came.
 */
export async function ensureSession(
  apiFetch: ApiFetch,
  session: unknown,
  idempotencyKey?: string,
): Promise<PostedSession> {
  try {
    return await postSession(apiFetch, session, idempotencyKey);
  } catch (err) {
    if (!isApiErrorLike(err) || err.status !== 409) throw err;
    const slug = apiErrorSlug(err);
    if (slug === "duplicate_id") return { existed: true };
    throw new SessionConflictError(slug);
  }
}

/**
 * A replay's session (#286): read by id first, posted only on a 404. The POST
 * dedups on its sort key, startedAt and id: once a correction made elsewhere
 * has moved startedAt, a replay that only posted would get a 201 and a second
 * session with the same id. The read asks for a consistent one
 * (`?consistent=1`); a 404 that races a POST lands on the POST's 409.
 */
async function resumeSession(
  apiFetch: ApiFetch,
  sessionId: string,
  session: unknown,
): Promise<PostedSession> {
  try {
    await apiFetch<unknown>(
      `/api/sessions/${encodeURIComponent(sessionId)}?consistent=1`,
    );
    return { existed: true };
  } catch (err) {
    if (!isApiErrorLike(err) || err.status !== 404) throw err;
  }
  return ensureSession(apiFetch, session);
}
